// components/DisplayCard.jsx

import {
  Box,
  Card,
  CardActions,
  Button
} from '@mui/material';
import { Copy } from 'lucide-react';
import { useEffect, useState } from 'react';

export default function DisplayCard({
  entry,
  onEdit,
  onDelete,
  onDuplicate,
  isDeleted,
  onClick,
  children,
  // permission-aware flags (default true so existing callers don't break)
  editAllowed = true,
  deleteAllowed = true,
  duplicateAllowed = true,
  // optional active colors
  editColor = 'primary.main',
  deleteColor = 'error.main',
  duplicateColor = '#9CA3AF',
  // optional per-button prop overrides
  editButtonProps = {},
  deleteButtonProps = {},
  duplicateButtonProps = {}
}) {
  const unifiedSX = (allowed, activeColor, extraSX) => ({
    color: allowed ? activeColor : 'lightgray',
    opacity: allowed ? 1 : 0.4,
    backgroundColor: 'transparent',
    '&:hover': { backgroundColor: 'transparent' },
    pointerEvents: 'auto',
    ...(extraSX || {})
  });

  const mergedEditProps = {
    ...editButtonProps,
    sx: unifiedSX(editAllowed, editColor, editButtonProps?.sx)
  };
  const mergedDupProps = {
    ...duplicateButtonProps,
    sx: {
      ...unifiedSX(duplicateAllowed, duplicateColor, duplicateButtonProps?.sx),
      ...(duplicateAllowed
        ? { color: '#9CA3AF', '&:hover': { backgroundColor: 'transparent', color: '#6B7280' } }
        : { color: 'rgba(0,0,0,0.15)' })
    }
  };
  const mergedDelProps = {
    ...deleteButtonProps,
    sx: unifiedSX(deleteAllowed, deleteColor, deleteButtonProps?.sx)
  };

  const imageUrl = entry.photos?.[0] || entry.imageUrls?.[0];
  const [dominant, setDominant] = useState('255,255,255');
  const OVERLAY_PX = 56; // amount of image bleed into the content area

  // Compute luminance from the dominant color (0 = dark, 1 = bright)
  const [dr, dg, db] = dominant.split(',').map((n) => parseInt(n, 10) || 255);
  const luminance = Math.max(0, Math.min(1, (0.2126 * dr + 0.7152 * dg + 0.0722 * db) / 255));
  // Darker image => slightly stronger blur + more brightening; brighter image => subtler
  const bleedBlurPx = 8 + (1 - luminance) * 6; // 8–14px
  const bleedBrightness = 0.95 + (1 - luminance) * 0.15; // 0.95–1.10
  // Shadow divider opacity stronger on bright images
  const shadowOpacity = 0.12 + 0.18 * luminance; // 0.12–0.30

  useEffect(() => {
    if (!imageUrl) { setDominant('255,255,255'); return; }
    let cancelled = false;
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = imageUrl;
      img.onload = () => {
        if (cancelled) return;
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const w = (canvas.width = 80);
          const h = (canvas.height = 80);
          ctx.drawImage(img, 0, 0, w, h);
          const data = ctx.getImageData(0, 0, w, h).data;
          let r = 0, g = 0, b = 0, count = 0;
          const step = 10; // sample every 10th pixel for speed
          for (let i = 0; i < data.length; i += 4 * step) {
            r += data[i];
            g += data[i + 1];
            b += data[i + 2];
            count++;
          }
          r = Math.round(r / Math.max(1, count));
          g = Math.round(g / Math.max(1, count));
          b = Math.round(b / Math.max(1, count));
          setDominant(`${r},${g},${b}`);
        } catch (e) {
          // Canvas may be tainted by CORS — fall back to white blend
          setDominant('255,255,255');
        }
      };
      img.onerror = () => setDominant('255,255,255');
    } catch (e) {
      setDominant('255,255,255');
    }
    return () => { cancelled = true; };
  }, [imageUrl]);

  const blendGradient = `linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(${dominant},0.35) 60%, rgba(255,255,255,0.92) 88%, rgba(255,255,255,1) 100%)`;

  return (
    <Card
      elevation={3}
      onClick={onClick}
      sx={{
        borderRadius: 2,
        textAlign: 'center',
        py: 0,
        maxWidth: 340,
        minWidth: 340,
        minHeight: 500,
        maxHeight: 500,
        
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        position: 'relative',
        opacity: isDeleted ? 0 : 1,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease, opacity 0.4s ease',
        boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 28px rgba(0,0,0,0.15)',
        }
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          left: 8,
          backgroundColor: entry.status === 'active' ? 'green' : 'red',
          color: 'white',
          px: 1,
          borderRadius: 1,
          fontSize: 12,
          fontWeight: 'bold'
        }}
      >
        {entry.status}
      </Box>

      {entry.isFeatured && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: 'goldenrod',
            color: 'white',
            px: 1,
            borderRadius: 1,
            fontSize: 12,
            fontWeight: 'bold'
          }}
        >
          Featured
        </Box>
      )}

      <Box sx={{ width: '100%', height: 200, position: 'relative', overflow: 'hidden', borderTopLeftRadius: 8, borderTopRightRadius: 8, marginBottom: -4 }}>
        {entry.photos?.[0] || entry.imageUrls?.[0] ? (
          <>
            <Box
              component="img"
              src={entry.photos?.[0] || entry.imageUrls?.[0]}
              alt={entry.title}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                filter: 'brightness(0.95)'
              }}
            />
          </>
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              backgroundColor: '#eee',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="lucide lucide-image" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
              <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="M21 15l-5-5L5 21" />
            </svg>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          position: 'relative',
          px: 2,
          pb: 2,
          pt: 2, // start content directly at the blurred area
          flexGrow: 1,
          textAlign: 'center',
          background: 'rgba(255,255,255,0.6)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: `${OVERLAY_PX * 1.6}px`, // extend bleed slightly for natural overlap
            backgroundImage: imageUrl ? `url(${imageUrl})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'bottom center',
            filter: `blur(${bleedBlurPx}px) brightness(${bleedBrightness})`,
            transform: 'scale(1.05)',
            opacity: 0.9,
            maskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)',
            pointerEvents: 'none',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: `${OVERLAY_PX}px`,
            background: `linear-gradient(to bottom, rgba(${dominant},0.25) 0%, rgba(255,255,255,0) 100%)`,
            pointerEvents: 'none',
          },
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 14,
            background: `linear-gradient(to bottom, rgba(0,0,0,${shadowOpacity}), rgba(0,0,0,0))`,
            pointerEvents: 'none'
          }}
        />
        {children}
      </Box>

      <CardActions sx={{ justifyContent: 'center' }}>
        <Button
          size="small"
          onClick={(e) => { e.stopPropagation(); onEdit(); }}
          {...mergedEditProps}
        >
          Edit
        </Button>
        <Button
          size="small"
          onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
          {...mergedDupProps}
        >
          <Copy size={16} />
        </Button>
        <Button
          size="small"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          {...mergedDelProps}
        >
          Delete
        </Button>
      </CardActions>
    </Card>
  );
}